import type { ChangeValue, Choice, Product, ProductData, SmartNumberDescription } from "@/features/sub-families/types";
import { createSupplyChoice, propertyToChoice } from "@/features/sub-families/utils/filters.ts";
import { getEmptyConfigCode } from "@/features/sub-families/utils/smart-number.ts";
import { getSupplies } from "@/lib/product.ts";
import { EXTERNAL_POWER } from "@/resources/smart-number.ts";
import { type Nullable, SmartNumberDescriptor } from "@/types";

export type ProductScenario = 1 | 2 | 3 | 4;

export const getProductScenario = (product: Nullable<Product>): Nullable<1 | 2 | 3 | 4> => {
    const powerSupplyIncluded = product?.properties.power_supply.workingTitle === "included";
    const isExternalPowerSupply = product?.properties.dimming_type.configCode === EXTERNAL_POWER;
    const converterIncluded = product?.properties.convector.workingTitle === "included"; // convector should be converter, however, no one dares to change this property anymore 😅
    const fixationSystem = product?.properties.fixation.workingTitle === "system";

    const isScenario1 = powerSupplyIncluded;
    const isScenario2 = isExternalPowerSupply;
    const isScenario3 = converterIncluded && !fixationSystem;
    const isScenario4 = converterIncluded && fixationSystem;

    switch (true) {
        case isScenario1:
            return 1;

        case isScenario2:
            return 2;

        case isScenario3:
            return 3;

        case isScenario4:
            return 4;

        default:
            return null;
    }
};

export const getPowerSupplyOptionsByProductScenario = (scenario: ProductScenario, product: Nullable<Product>) => {
    switch (scenario) {
        case 2:
        case 3:
            return getSupplyOptions(product);

        default:
            return [];
    }
};

export const getDimTypePowerSupplyResetMappingByProductScenario = (
    productScenario: ProductScenario,
    smartNumberDescription: Nullable<SmartNumberDescription>,
): Nullable<ChangeValue> => {
    if (!smartNumberDescription) return null;

    switch (productScenario) {
        case 2: {
            const configCode = getEmptyConfigCode(SmartNumberDescriptor.PowerSupply, smartNumberDescription);
            if (!configCode) return null;

            return { descriptor: SmartNumberDescriptor.PowerSupply, configCode };
        }

        default:
            return null;
    }
};

export const getDimmingTypeOptions = (products: ProductData) => {
    const choices: Choice[] = [];

    for (const product of Object.values(products)) {
        const dimmingType = product?.properties.dimming_type;
        if (!dimmingType) continue;

        const alreadyPresent = choices.some((option) => option.configCode === dimmingType.configCode); // prettier-ignore
        if (alreadyPresent) continue;

        const choice = propertyToChoice(dimmingType);
        if (!choice) continue;

        choices.push(choice);
    }

    return choices;
};

export const getSupplyOptions = (product: Nullable<Product>) => {
    return getSupplies(product).map(createSupplyChoice);
};

export function getProductSupply(product: Nullable<Product>) {
    return getSupplies(product)[0] ?? null;
}
